/**
 * Post detail (M2.1): a bottom sheet on phones, a side panel on desktop. GET /api/publications/:id + /history rendered as a
 * plain-language timeline, the attempts, the times (the owner's time zone), the metadata and the actions the API allows.
 */
import { useState } from "react";
import type { Publication, PublicationHistory, ThumbnailStatus } from "../../api/types";
import { Icon } from "../../components/Icon";
import { Badge, Button, Modal, QueryView } from "../../components/ui";
import { usePublication, usePublicationHistory } from "../../data/queries";
import { dateTime, languageName } from "../../lib/format";
import { browserTimeZone } from "../../lib/tz";
import { CoverPicker } from "../review/CoverPicker";
import { AttentionActions, BlockerLink, PubSheets, openPublication, usePubActions } from "./PublicationRow";
import { attentionInfo, canCancel, canEditMetadata, canReschedule, canRetry, needsAttention, platformName, stateChip } from "./pubState";

export function PublicationDetail({ id }: { id: string }) {
  const q = usePublication(id);
  return (
    <Modal variant="panel" title={q.data ? `Ep ${q.data.episode.sequence} · ${q.data.metadata.title || q.data.episode.title}` : "Post"} onClose={() => openPublication(null)}>
      <QueryView q={q}>{(p) => <DetailBody p={p} />}</QueryView>
    </Modal>
  );
}

const TONE = { info: "info", good: "ok", warn: "attention", bad: "danger" } as const;

function DetailBody({ p }: { p: Publication }) {
  const h = usePublicationHistory(p.id);
  const a = usePubActions(p);
  const [sheet, setSheet] = useState<"reschedule" | "cancel" | "edit" | null>(null);
  const chip = stateChip(p);
  const attention = needsAttention(p) || !!p.hold_detail;
  const info = attention ? attentionInfo(p) : null;
  const tz = browserTimeZone();
  const m = p.metadata;
  return (
    <div className="pub-detail">
      <div className="pub-detail-head">
        <Badge tone={chip.tone}>{chip.label}</Badge>
        <span className="muted small">{p.series.title} · {languageName(p.language_code)} · {platformName(p.platform)} {p.account_name}</span>
      </div>

      {info && (
        <section className={`pub-detail-hold tone-${p.hold_detail?.waiting ? "attention" : chip.tone === "danger" ? "danger" : "attention"}`} aria-label="Why it's waiting">
          <p>{info.reason}</p>
          <BlockerLink p={p} />
          <div className="pub-actions"><AttentionActions p={p} a={a} setSheet={setSheet} /></div>
        </section>
      )}

      <dl className="pub-times" aria-label={`Times (${tz})`}>
        <div><dt>Scheduled</dt><dd>{p.scheduled_at ? dateTime(p.scheduled_at) : "As soon as possible"}</dd></div>
        {p.published_at && <div><dt>Published</dt><dd>{dateTime(p.published_at)}</dd></div>}
        {p.verified_at && <div><dt>Verified live</dt><dd>{dateTime(p.verified_at)}</dd></div>}
        {p.next_attempt_at && <div><dt>Next try</dt><dd>{dateTime(p.next_attempt_at)}</dd></div>}
        <div><dt>Attempts</dt><dd>{p.attempt_count}</dd></div>
        {p.thumbnail && <div><dt>Thumbnail</dt><dd>{thumbnailText(p.thumbnail)}</dd></div>}
        {p.platform_post_id && (
          <div><dt>Series playlist</dt><dd>{p.playlist?.item_id ? "In the playlist" : p.playlist ? "Not added yet" : "Not created yet"}</dd></div>
        )}
      </dl>
      <p className="muted small">Times in {tz}.</p>

      <div className="pub-actions">
        {p.platform_url && <a className="btn btn-secondary btn-sm" href={p.platform_url} target="_blank" rel="noreferrer"><Icon name="external" size={18} /><span>Open on {platformName(p.platform)}</span></a>}
        {p.playlist?.url && <a className="btn btn-secondary btn-sm" href={p.playlist.url} target="_blank" rel="noreferrer"><Icon name="book" size={18} /><span>Series playlist</span></a>}
        {!info && canRetry(p) && <Button size="sm" variant="ghost" icon="refresh" busy={a.retry.pending} onClick={() => void a.retry.mutate()}>Retry</Button>}
        {canReschedule(p) && <Button size="sm" variant="ghost" icon="calendar" onClick={() => setSheet("reschedule")}>Reschedule</Button>}
        {canEditMetadata(p) && <Button size="sm" variant="ghost" icon="edit" onClick={() => setSheet("edit")}>Edit details</Button>}
        {canCancel(p) && info?.action !== "cancel" && <Button size="sm" variant="ghost" onClick={() => setSheet("cancel")}>Cancel post</Button>}
      </div>

      {/* Choosing here refreshes this post (its thumbnail state) as well as the version. */}
      <CoverPicker versionId={p.version_id} invalidate={[`publication/${p.id}`, "publications"]} hint="The chosen cover becomes this video's YouTube thumbnail (and of the episode's other posts)." />

      <section aria-labelledby="pd-timeline">
        <h3 id="pd-timeline" className="qr-sub">Timeline</h3>
        <QueryView q={h}>{(hist) => <Timeline h={hist} />}</QueryView>
      </section>

      {h.data && h.data.attempts.length > 0 && <Attempts h={h.data} />}

      <section aria-labelledby="pd-meta">
        <h3 id="pd-meta" className="qr-sub">Details sent to {platformName(p.platform)}</h3>
        <dl className="meta-inner">
          <div><dt>Title</dt><dd>{m.title}</dd></div>
          {m.description && <div><dt>Description</dt><dd className="pre">{m.description}</dd></div>}
          {m.tags.length > 0 && <div><dt>Tags</dt><dd>{m.tags.join(", ")}</dd></div>}
          <div><dt>Visibility</dt><dd>{m.privacy}{m.made_for_kids ? " · made for kids" : ""}{m.contains_synthetic_media ? " · labelled AI-altered" : ""}</dd></div>
          <div><dt>Notify subscribers</dt><dd>{m.notify_subscribers ? "Yes" : "No"}</dd></div>
        </dl>
      </section>
      <PubSheets p={p} sheet={sheet} setSheet={setSheet} a={a} />
    </div>
  );
}

/** The post's thumbnail in plain words; not_eligible / failed carry the worker's own message (what to do about it). */
export function thumbnailText(t: ThumbnailStatus): string {
  switch (t.state) {
    case "none": return "No cover chosen";
    case "pending": return "Waiting to be set";
    case "set": return "Set on YouTube";
    case "not_eligible": return t.message || "This channel can't use custom thumbnails yet.";
    case "failed": return t.message || "YouTube didn't accept the cover.";
  }
}

export function Timeline({ h }: { h: PublicationHistory }) {
  if (!h.lines.length) return <p className="muted small">Nothing recorded yet.</p>;
  return (
    <ol className="timeline">
      {h.lines.map((l, i) => (
        <li key={`${l.at}-${i}`} className={`tone-${TONE[l.tone]}`}>
          <span className="timeline-dot" aria-hidden />
          <span className="timeline-body">
            <span>{l.text}</span>
            <span className="muted small"><time dateTime={l.at}>{dateTime(l.at)}</time>{l.actor && !l.actor.startsWith("publisher") ? ` · ${l.actor.replace(/^user:.*/, "you")}` : ""}{l.attempt ? ` · attempt ${l.attempt}` : ""}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function Attempts({ h }: { h: PublicationHistory }) {
  return (
    <section aria-labelledby="pd-attempts">
      <h3 id="pd-attempts" className="qr-sub">Attempts</h3>
      <div className="table-wrap">
        <table className="data-table">
          <thead><tr><th scope="col">#</th><th scope="col">Started</th><th scope="col">Finished</th><th scope="col">Outcome</th><th scope="col">Error</th></tr></thead>
          <tbody>
            {h.attempts.map((x) => (
              <tr key={x.attempt}>
                <td>{x.attempt}</td><td>{dateTime(x.started_at)}</td><td>{x.finished_at ? dateTime(x.finished_at) : "—"}</td>
                <td>{[x.op, x.outcome].filter(Boolean).join(" · ") || "—"}</td><td>{x.error?.message ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
